import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import RecordRTC from "recordrtc";
import { FaceDetection } from "@mediapipe/face_detection";
import { Camera } from "@mediapipe/camera_utils";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Navbar from "./Navbar";
import "../App.css";


const RECORD_SECONDS = 30;
const SQI_THRESHOLD = 0.5;


const speak = (text, lang) => {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();

  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = lang === "hi" ? "hi-IN" : "en-US";
  utter.rate = 0.9;
  utter.pitch = 1;
  utter.volume = 1;

  window.speechSynthesis.speak(utter);
};

const generateSpeechText = (v, lang) => {
  if (!v) return "";
  const message=getMedicalInsights(v, lang)
  console.log(message,"message")
  if (lang === "hi") {
    return `
आपकी स्वास्थ्य रिपोर्ट तैयार है।
आपकी हृदय गति ${v.heartRate} प्रति मिनट है।
${message[0].title}
${message[0].text}

यह जानकारी केवल जागरूकता के लिए है, यह मेडिकल सलाह नहीं है।
`;
  }

  return `
Your health report is ready.
${message[0].title}
${message[0].text}

This information is for awareness only and not a medical diagnosis.
`;
};

const SIMPLE_DISCLAIMER = {
  en: "This information is for educational purposes and is not a substitute for professional medical advice. Always consult a healthcare professional for diagnosis and treatment.",
  hi: "यह जानकारी केवल शैक्षिक उद्देश्यों के लिए है और पेशेवर चिकित्सा सलाह का विकल्प नहीं है। निदान और उपचार के लिए हमेशा योग्य डॉक्टर से परामर्श करें।"
};


/* ================= LANGUAGE TEXT ================= */

const HRV_TEXT = {
  en: {
    LOW: "Low",
    HEALTHY: "Healthy",
    OPTIMAL: "Optimal",

    RELAXED: "Relaxed",
    BALANCED: "Balanced",
    STRESSED: "Stressed",

    SDNN_DESC:
      "The standard deviation of all NN intervals (time between each heartbeat). Higher values usually indicate that your body is coping better with stress.",

    RMSSD_DESC:
      "The root mean square of successive intervals between normal heartbeats. Higher values indicate better recovery and a relaxed nervous system.",

    LFHF_DESC:
      "The LF/HF ratio reflects the balance between stress (fight-or-flight) and relaxation (rest-and-digest).",
  },

  hi: {
    LOW: "कम",
    HEALTHY: "सामान्य",
    OPTIMAL: "उत्तम",

    RELAXED: "आराम",
    BALANCED: "संतुलित",
    STRESSED: "तनाव",

    SDNN_DESC:
      "यह प्रत्येक दिल की धड़कन के बीच के समय का मानक विचलन दर्शाता है। अधिक मान यह दर्शाता है कि शरीर तनाव को बेहतर तरीके से संभाल रहा है।",

    RMSSD_DESC:
      "यह लगातार दिल की धड़कनों के बीच के अंतर को दर्शाता है। अधिक मान बेहतर रिकवरी और शांत नर्वस सिस्टम को दर्शाता है।",

    LFHF_DESC:
      "यह तनाव (फाइट-या-फ्लाइट) और आराम (रेस्ट-एंड-डाइजेस्ट) के बीच संतुलन को दर्शाता है।",
  },
};


const DISCLAIMER_TEXT = {
  en: {
    header: "⚠️ Important Health Disclaimer",
    intro: (
      <>
        The following HRV (Heart Rate Variability) information is provided for
        <strong> general awareness and educational purposes only</strong>.
        It is <strong>not a medical diagnosis or treatment</strong>.
      </>
    ),

    rmssd: {
      title: "RMSSD",
      points: [
        { text: "Below 20 ms – Reduced recovery", color: "yellow" },
        { text: "20–50 ms – Normal range", color: "green" },
        { text: "Above 50 ms – Good recovery", color: "darkgreen" },
      ],
    },

    sdnn: {
      title: "SDNN",
      points: [
        { text: "Below 50 ms – Higher risk", color: "red" },
        { text: "50–100 ms – Moderate range", color: "yellow" },
        { text: "Above 100 ms – Healthy variability", color: "green" },
      ],
    },

    footer:
      "⚕️ Always consult a qualified healthcare professional for medical advice.",

    ctaTitle: "🌐 Manage Your Health Better with AICA",
    ctaDesc:
      "Take better care of yourself and your loved ones with 24×7 AI-powered health support.",
    ctaBtn: "Visit qcares.aicahealth.com →",
    ctaSub:
      "AICA acts as your Personal Care Assistant, helping you get the right guidance for your healthcare needs — anytime, anywhere, even when a doctor is not nearby.",
  },

  hi: {
    header: "⚠️ महत्वपूर्ण स्वास्थ्य सूचना",
    intro: (
      <>
        नीचे दी गई HRV (हृदय गति परिवर्तनशीलता) जानकारी केवल
        <strong> सामान्य जागरूकता और शैक्षिक उद्देश्य</strong> के लिए है।
        यह <strong>मेडिकल निदान या उपचार नहीं</strong> है।
      </>
    ),

    rmssd: {
      title: "RMSSD",
      points: [
        { text: "20 ms से कम – रिकवरी कम हो सकती है", color: "yellow" },
        { text: "20–50 ms – सामान्य सीमा", color: "green" },
        { text: "50 ms से अधिक – अच्छी रिकवरी", color: "darkgreen" },
      ],
    },

    sdnn: {
      title: "SDNN",
      points: [
        { text: "50 ms से कम – अधिक जोखिम", color: "red" },
        { text: "50–100 ms – मध्यम स्तर", color: "yellow" },
        { text: "100 ms से अधिक – स्वस्थ परिवर्तनशीलता", color: "green" },
      ],
    },

    footer:
      "⚕️ स्वास्थ्य से संबंधित किसी भी निर्णय के लिए कृपया योग्य चिकित्सक से परामर्श लें।",

    ctaTitle: "🌐 AICA के साथ अपने स्वास्थ्य को बेहतर प्रबंधित करें",
    ctaDesc:
      "अपने और अपने परिवार के स्वास्थ्य की बेहतर देखभाल करें 24×7 AI आधारित सहायता के साथ।",
    ctaBtn: "qcares.aicahealth.com पर जाएँ →",
    ctaSub:
      "AICA आपका पर्सनल केयर असिस्टेंट है, जो आपको स्वास्थ्य संबंधी सही मार्गदर्शन देता है — कभी भी, कहीं भी, जब डॉक्टर पास न हों।",
  },
};



const MEDICAL_TEXT = {
  en: {
    startScan: "Start 30s Scan",
    analyze: "Analyze My Health",
    analyzing: "Analyzing...",
    recordAgain: "Record Again",
    scanAgain: "Scan Again",
    report: "Your Health Report",
    hr: "Heart Rate",
    bp: "Blood Pressure",

    disclaimer: `⚠️ Important Disclaimer

The following HRV interpretation ranges are provided for general awareness and educational purposes only. 
They are not intended for medical diagnosis or treatment.

RMSSD (Root Mean Square of Successive Differences):
• < 20 ms  → Reduced parasympathetic activity (potential risk)
• 20–50 ms → Typical / normal range
• > 50 ms  → Good parasympathetic (vagal) function

SDNN (Standard Deviation of NN Intervals):
• < 50 ms   → Higher risk (associated with reduced HRV)
• 50–100 ms → Moderate risk range
• > 100 ms  → Lower risk (generally healthy autonomic function)

⚠️ This information does not replace professional medical advice.
Please consult a qualified healthcare professional for clinical evaluation.`,

    LOW_BP: {
      title: "Low Blood Pressure",
      text: "Your blood pressure appears low (below 90/60 mmHg). This may cause dizziness or fatigue.",
    },

    CRISIS: {
      title: "Hypertensive Crisis",
      text: "Blood pressure above 180/120 mmHg is a medical emergency. Seek immediate medical care.",
    },

    ELEVATED: {
      title: "Elevated Blood Pressure",
      text: "Your blood pressure is slightly elevated. Lifestyle changes may help.",
    },

    STAGE_1: {
      title: "Hypertension – Stage 1",
      text: "Mildly elevated blood pressure. Monitoring and lifestyle changes are advised.",
    },

    STAGE_2: {
      title: "Hypertension – Stage 2",
      text: "High blood pressure detected. Please consult a doctor.",
    },

    GOOD_HEART: {
      title: "Good Heart Health",
      text: "Healthy HRV indicate good cardiovascular balance.",
    },

    LOW_HRV: {
      title: "Low HRV",
      text: "Low HRV may be associated with stress, fatigue, or reduced recovery.",
    },

    LOW_HRV_BP: {
      title: "Low HRV",
      text: "Combined indicators suggest increased cardiovascular strain. Medical consultation is recommended.",
    },
  },

  hi: {
    startScan: "30 सेकंड स्कैन शुरू करें",
    analyze: "स्वास्थ्य विश्लेषण करें",
    analyzing: "विश्लेषण हो रहा है...",
    recordAgain: "फिर से रिकॉर्ड करें",
    scanAgain: "फिर से स्कैन करें",
    report: "आपकी स्वास्थ्य रिपोर्ट",
    hr: "हृदय गति",
    bp: "ब्लड प्रेशर",
    disclaimer: `⚠️ महत्वपूर्ण सूचना

नीचे दिए गए HRV (Heart Rate Variability) मान केवल सामान्य जागरूकता और शैक्षिक उद्देश्य के लिए हैं।
यह जानकारी किसी भी प्रकार के मेडिकल निदान या उपचार का विकल्प नहीं है।

RMSSD (रूट मीन स्क्वेयर ऑफ सक्सेसिव डिफरेंसेज़):
• 20 ms से कम  → पैरासिम्पैथेटिक गतिविधि कम हो सकती है (संभावित जोखिम)
• 20–50 ms     → सामान्य सीमा
• 50 ms से अधिक → अच्छी पैरासिम्पैथेटिक (वागल) कार्यक्षमता

SDNN (NN इंटरवल का मानक विचलन):
• 50 ms से कम   → अधिक जोखिम (HRV कम होने से जुड़ा)
• 50–100 ms     → मध्यम जोखिम
• 100 ms से अधिक → कम जोखिम (आमतौर पर अच्छी ऑटोनोमिक कार्यक्षमता)

⚠️ यह जानकारी पेशेवर चिकित्सा सलाह का विकल्प नहीं है।
स्वास्थ्य से संबंधित किसी भी निर्णय के लिए कृपया योग्य चिकित्सक से परामर्श लें।`,


    LOW_BP: {
      title: "लो ब्लड प्रेशर",
      text: "आपका ब्लड प्रेशर कम लग रहा है। इससे चक्कर या कमजोरी हो सकती है।",
    },

    CRISIS: {
      title: "हाइपरटेंसिव क्राइसिस",
      text: "180/120 mmHg से अधिक BP मेडिकल इमरजेंसी है। तुरंत डॉक्टर से संपर्क करें।",
    },

    ELEVATED: {
      title: "थोड़ा बढ़ा हुआ BP",
      text: "आपका ब्लड प्रेशर थोड़ा बढ़ा हुआ है। जीवनशैली में सुधार मदद कर सकता है।",
    },

    STAGE_1: {
      title: "हाइपरटेंशन – स्टेज 1",
      text: "ब्लड प्रेशर थोड़ा अधिक है। निगरानी आवश्यक है।",
    },

    STAGE_2: {
      title: "हाइपरटेंशन – स्टेज 2",
      text: "ब्लड प्रेशर काफी अधिक है। डॉक्टर से सलाह लें।",
    },

    GOOD_HEART: {
      title: "अच्छा हार्ट हेल्थ",
      text: "HRV अच्छा है, जो हृदय स्वास्थ्य को दर्शाता है।",
    },

    LOW_HRV: {
      title: "लो HRV",
      text: "तनाव, थकान या रिकवरी कम होने का संकेत हो सकता है।",
    },

    LOW_HRV_BP: {
      title: "लो HRV और हाई BP",
      text: "संयुक्त जोखिम संकेत पाए गए। डॉक्टर से संपर्क करें।",
    },
  },
};

/* ================= MEDICAL LOGIC ================= */

function classifyBP(sbp, dbp) {
  if (sbp < 90 || dbp < 60) return "LOW";
  if (sbp >= 180 || dbp >= 120) return "CRISIS";
  if (sbp >= 140 || dbp >= 90) return "STAGE_2";
  if ((sbp >= 130 && sbp <= 139) || (dbp >= 80 && dbp <= 89)) return "STAGE_1";
  if (sbp >= 120 && sbp <= 129 && dbp < 80) return "ELEVATED";
  return "NORMAL";
}

function classifyHRV(SDNN, RMSSD) {
  if (SDNN >= 50 && RMSSD >= 50) return "HIGH";
  if (SDNN < 50 && RMSSD < 20) return "LOW";
  return "MODERATE";
}


function getMedicalInsights(v, lang) {
  if (!v) return [];
  const T = MEDICAL_TEXT[lang];
  const hrv = classifyHRV(v.SDNN, v.RMSSD);

  const messages = [];

  if (hrv === "HIGH")
    messages.push({ type: "success", ...T.GOOD_HEART });

  if (hrv === "LOW")
    messages.push({ type: "danger", ...T.LOW_HRV_BP });

  if (hrv === "LOW")
    messages.push({ type: "warning", ...T.LOW_HRV });

  return messages;
}

/* ================= APP ================= */

export default function App() {
  const webcamRef = useRef(null);
  const recorderRef = useRef(null);
  const navigate = useNavigate();

  const [faceDetected, setFaceDetected] = useState(false);
  const faceDetectedRef = useRef(false);
  const [faceError, setFaceError] = useState(false);
  const [sqiError, setSqiError] = useState(false);

  const [lang, setLang] = useState("en");
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(RECORD_SECONDS);
  const [videoBlob, setVideoBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const videoPlayerRef = useRef(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [useRecordingBg, setUseRecordingBg] = useState(false);
  const [vitals, setVitals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waveGraphData, setWaveGraphData] = useState([]);

  const t = HRV_TEXT[lang];

  useEffect(() => {
    if (vitals) {
      const speechText = generateSpeechText(vitals, lang);
      console.log(speechText, "speechText")
      speak(speechText, lang);
    }
  }, [vitals, lang]);


   useEffect(() => {
    let camera;

    const initFaceDetection = () => {
      if (!webcamRef.current?.video || webcamRef.current.video.readyState !== 4) return;

      const video = webcamRef.current.video;

      const faceDetection = new FaceDetection({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
      });

      faceDetection.setOptions({
        model: "short",
        minDetectionConfidence: 0.5,
      });

      faceDetection.onResults((results) => {
        const detected = results.detections?.length > 0;
        faceDetectedRef.current = detected;
        setFaceDetected(detected);
      });

      camera = new Camera(video, {
        onFrame: async () => {
          if (video) await faceDetection.send({ image: video });
        },
        width: 640,
        height: 480,
      });

      camera.start();
    };

    const interval = setInterval(() => {
      if (webcamRef.current?.video?.readyState === 4) {
        clearInterval(interval);
        initFaceDetection();
      }
    }, 500);

    return () => {
      if (camera) camera.stop();
      clearInterval(interval);
    };
  }, []);




  const mapLabel = (label) =>
    label === "Low"
      ? t.LOW
      : label === "Healthy"
        ? t.HEALTHY
        : t.OPTIMAL;


 

  const getSDNNStatus = (v) => {
    console.log(v, "vv0999999999999999")
    if (v < 30) return { label: "Low", active: "Low", color: "#fbbc05" };
    if (v <= 96) return { label: "Healthy", active: "Healthy", color: "#34a853" };
    return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
  };



  const getRMSSDStatus = (v) => {
    if (v < 20) return { label: "Low", active: "Low", color: "#fbbc05" };
    if (v <= 90) return { label: "Healthy", active: "Healthy", color: "#34a853" };
    return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
  };

  const getLFHFStatus = (lfhf) => {
  if (lfhf < 0.5) {
    // Very low LF/HF → dominant parasympathetic → relaxed
    return { label: "Optimal", active: "Relaxed", color: "#fbbc05" };
  } else if (lfhf >= 0.5 && lfhf <= 1.5) {
    // Balanced sympathetic/parasympathetic activity
    return { label: "Healthy", active: "Balanced", color: "#34a853" };
  } else { 
    // High LF/HF → dominant sympathetic → stressed
    return { label: "High Stress", active: "Stressed", color: "#ea4335" };
  }
};

  const formatNum = (v, digits = 0) =>
    typeof v === "number" && !isNaN(v) ? v.toFixed(digits) : "--";

// const startRecording = () => {
//     if (!faceDetectedRef.current) {
//       alert("Face Not Detected. Please look at the camera.");
//       return;
//     }

//     setFaceError(false);
//     setSqiError(false);
//     setVitals(null);

//     const stream = webcamRef.current?.stream;
//     if (!stream) return alert("Webcam not available");

//     recorderRef.current = new RecordRTC(stream, {
//   type: "video",
//   mimeType: "video/mp4",
//   bitsPerSecond: 8000000,   // increase bitrate
// });

//     recorderRef.current.startRecording();
//     setIsRecording(true);

//     let t = RECORD_SECONDS;
//     setTimer(t);

//     const interval = setInterval(() => {
//       t -= 1;
//       setTimer(t);

//       if (!faceDetectedRef.current) {
//         clearInterval(interval);
//         recorderRef.current.stopRecording(() => recorderRef.current.destroy());
//         setIsRecording(false);
//         setFaceError(true);
//         return;
//       }

//       if (t <= 0) {
//         clearInterval(interval);
//         stopRecording();
//       }
//     }, 1000);
//   };

const startRecording = () => {
  if (!faceDetectedRef.current) {
    speak(
      lang === "hi"
        ? "कृपया कैमरे की ओर देखें, आपका चेहरा दिखाई नहीं दे रहा है"
        : "Please look at the camera. Face not detected",
      lang
    );
    return;
  }

  // 🔥 NEW VOICE GUIDANCE
  speak(
    lang === "hi"
      ? "स्कैन शुरू हो रहा है। कृपया कैमरे की ओर सीधे देखें और बिलकुल स्थिर रहें। सिर या शरीर को न हिलाएं।"
      : "Scan started. Please look straight into the camera and do not move your head or body.",
    lang
  );

  setFaceError(false);
  setSqiError(false);
  setVitals(null);

  const stream = webcamRef.current?.stream;
  if (!stream) return alert("Webcam not available");

  recorderRef.current = new RecordRTC(stream, {
    type: "video",
    mimeType: "video/mp4",
    bitsPerSecond: 8000000,
  });

  recorderRef.current.startRecording();
  setIsRecording(true);

  let t = RECORD_SECONDS;
  setTimer(t);

  const interval = setInterval(() => {
    t -= 1;
    setTimer(t);

    // 🔥 MID GUIDANCE
    if (t === 20) {
      speak(
        lang === "hi"
          ? "अच्छा चल रहा है, कृपया स्थिर रहें"
          : "Good, please stay still",
        lang
      );
    }

    if (t === 5) {
      speak(
        lang === "hi"
          ? "रिकॉर्डिंग समाप्त होने वाली है, हिलें नहीं"
          : "Almost done, do not move",
        lang
      );
    }

    if (!faceDetectedRef.current) {
      clearInterval(interval);
      recorderRef.current.stopRecording(() =>
        recorderRef.current.destroy()
      );
      setIsRecording(false);
      setFaceError(true);

      speak(
        lang === "hi"
          ? "चेहरा हट गया है, कृपया दोबारा कोशिश करें"
          : "Face lost. Please try again",
        lang
      );

      return;
    }

    if (t <= 0) {
      clearInterval(interval);
      stopRecording();
    }
  }, 1000);
};

  const stopRecording = () => {
    recorderRef.current.stopRecording(() => {
      const blob = recorderRef.current.getBlob();
      setVideoBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setUseRecordingBg(true);
      setIsRecording(false);
    });
  };

    const togglePreviewPlay = () => {
      const vid = videoPlayerRef.current;
      if (!vid) return;
      if (vid.paused) {
        vid.play();
        setIsPreviewPlaying(true);
      } else {
        vid.pause();
        setIsPreviewPlaying(false);
      }
    };

    const downloadVideo = () => {
      if (!videoBlob) return;
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scan_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };

    const replayRecording = () => {
      setVideoBlob(null);
      setPreviewUrl("");
      setIsPreviewPlaying(false);
      setVitals(null);
      setUseRecordingBg(false);
    };

  /* ===== HANDLE SUBMIT ===== */

  const handleSubmit = async () => {
    if (!videoBlob) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("file", videoBlob);
    formData.append("patient_id", localStorage.getItem("patient_id"))
    formData.append("phone_number", localStorage.getItem("patient_phone_number"));

    try {
      const res = await fetch(
        "https://lovencare.gopillz.com/api/analyze",
        { method: "POST", body: formData }
      );

      const data = await res.json();
      console.log(data, "data")
      const sqi = data?.sqi ?? 0;

      if (sqi < SQI_THRESHOLD) {
        setSqiError(true);
        setVideoBlob(null);
        setPreviewUrl("");
        setLoading(false);
        return;
      }

       setWaveGraphData(
          (data.waveform || []).map((v, i) => ({ time: i, value: v }))
        );

      setVitals({
          heartRate: Math.round(data.heart_rate_bpm_final || 0),
          SDNN: data?.hrv_time?.SDNN_ms || 0,
          RMSSD: data?.hrv_time?.RMSSD_ms || 0,
          LFHF: data?.hrv_freq?.["LF/HF"] || 0,
          rr:data?.rr??null,
          blood_pressure:data?.blood_pressure??null


      })

    } catch (err) {
      alert("Upload failed");
    }

    setLoading(false);
  };


  const T = MEDICAL_TEXT[lang];
  const D = DISCLAIMER_TEXT[lang]; 

  return (

      <div className="container">
        <Navbar />

      <div className="lang-toggle">
        <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>English</button>
        <button className={lang === "hi" ? "active" : ""} onClick={() => setLang("hi")}>हिन्दी</button>
      </div>

      {!videoBlob && (
//           <div className="rppg-card">
//   <div className="camera-wrapper">
//     <Webcam
//   ref={webcamRef}
//   className="rppg-webcam"
//   videoConstraints={{
//   width: { ideal: 1280 },
//   height: { ideal: 720 },
//   frameRate: { ideal: 30, min: 30 },
//   facingMode: "user"
// }}
// />

//     {!faceDetected && (
//       <div className="face-overlay">
//         ⚠ Face Not Detected
//       </div>
//     )}
//   </div>

//   {!isRecording ? (
//     <button
//       className="primary-btn"
//       onClick={startRecording}
//       disabled={!faceDetected}
//     >
//       ▶ Start 30s Scan
//     </button>
//   ) : (
//     <div className="timer-circle">{timer}s</div>
//   )}
// </div>
<div className="bg-gradient-to-br from-teal-500 via-green-400 to-blue-300 min-h-screen flex items-center justify-center p-4">
  <div className="w-full max-w-4xl mx-auto bg-white/90 rounded-3xl shadow-2xl p-6">

    {/* Camera */}
    <div className="relative w-full h-[550px] rounded-2xl overflow-hidden">

      <Webcam
        ref={webcamRef}
        className="w-full h-full object-cover"
        videoConstraints={{
          width: 1280,
          height: 720,
          facingMode: "user",
        }}
      />

      {/* Face Overlay */}
      {!faceDetected && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold">
          ⚠ Face Not Detected
        </div>
      )}

      {/* Recording Overlay */}
      {isRecording && (
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm animate-pulse">
          ● Recording
        </div>
      )}

    </div>

    {/* Timer Circle */}
    {isRecording && (
      <div className="mt-6 flex justify-center">
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 transform -rotate-90">
            <circle cx="48" cy="48" r="36" strokeWidth="8" stroke="#e6f7f4" fill="none" />
            <circle
              cx="48"
              cy="48"
              r="36"
              strokeWidth="8"
              stroke="#0ea5a4"
              strokeLinecap="round"
              fill="none"
              strokeDasharray={226.195}
              strokeDashoffset={(1 - ((RECORD_SECONDS - timer) / RECORD_SECONDS)) * 226.195}
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-xl font-bold text-teal-700">{timer}s</div>
            <div className="text-xs text-gray-500">remaining</div>
          </div>
        </div>
      </div>
    )}

    {/* Button */}
    {!isRecording && (
      <button
        onClick={startRecording}
        disabled={!faceDetected}
        className="mt-6 w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-xl font-semibold shadow-md hover:scale-105 transition"
      >
        ▶ Start 30s Scan
      </button>
    )}

    {/* Instruction */}
    <p className="text-sm text-gray-600 mt-4">
      Stay still, keep your face centered, and ensure good lighting
    </p>

  </div>
</div>

        )}
        {videoBlob && !vitals && (
  <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-400 to-blue-300 flex items-center justify-center p-4">

    {/* Card */}

    <div className="w-full max-w-4xl mx-auto  bg-white backdrop-blur-xl border border-white rounded-3xl shadow-2xl p-6">

      {/* Video Wrapper (IMPORTANT: relative) */}
      <div className="relative w-full h-[550px] rounded-2xl overflow-hidden">

        <video
          ref={videoPlayerRef}
          src={previewUrl}
          className="w-full h-full object-cover"
          onPlay={() => setIsPreviewPlaying(true)}
          onPause={() => setIsPreviewPlaying(false)}
        />

        {/* Play / Pause Button CENTERED */}
        <button
          onClick={togglePreviewPlay}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="w-16 h-16 rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-black shadow-lg hover:scale-110 transition">
            {isPreviewPlaying ? "⏸" : "▶"}
          </div>
        </button>

      </div>

      {/* Buttons INSIDE CARD */}
      <div className="flex gap-3 mt-5">

        <button
          className="flex-1 bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-xl font-semibold shadow-md hover:scale-105 hover:shadow-lg transition"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Analyzing..." : "Analyze"}
        </button>

        <button
          className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition shadow-sm"
          onClick={downloadVideo}
        >
          ⬇
        </button>

        <button
          className="px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition shadow-sm"
          onClick={replayRecording}
        >
          ↺
        </button>

      </div>

      {/* Instruction */}
     <p className="text-xs text-gray-600 mt-4 text-center">
  Review your recording before analyzing
</p>

    </div>
  </div>
)}



        {sqiError && (
          <div className="alert-box">
            ⚠ Signal quality too low. Use better lighting and stay still.
          </div>
        )}
        
        {vitals && (
          <div className="card">
            <h2>{T.report}</h2>

            <div className="vitals-grid">
              <div className="vital-box">❤️ {vitals.heartRate} <small>{T.hr}</small></div> 
              <div className="vital-box">
  🫁 {vitals.rr ? vitals.rr.toFixed(1) : "--"} 
  <small>Respiratory Rate (bpm)</small>
</div>
               {/* <div className="vital-box">🫁 {vitals.spo2}% <small>SpO₂</small></div> */}
              <div className="vital-box">🩸 {vitals.blood_pressure.sbp}/{vitals.blood_pressure.dbp} <small>{T.bp}</small></div>
            </div>

            {getMedicalInsights(vitals, lang).map((m, i) => (
              <div key={i} className={`alert ${m.type}`}>
                <strong>{m.title}</strong>
                <p>{m.text}</p>
              </div>
            ))}

            {(() => {
              const s = getSDNNStatus(vitals.SDNN);

              return (
                <div className="health-card">
                  <span className="pill" style={{ background: s.color }}>
                    {mapLabel(s.label)}
                  </span>

                  <h2>{formatNum(vitals.SDNN)} <span>ms</span></h2>

                  <div className="scale">
                    <span className={s.active === "Low" ? "active" : ""}>{t.LOW}</span>
                    <span className={s.active === "Healthy" ? "active" : ""}>{t.HEALTHY}</span>
                    <span className={s.active === "Optimal" ? "active" : ""}>{t.OPTIMAL}</span>
                  </div>

                  <small>{t.SDNN_DESC}</small>
                </div>
              );
            })()}


            {/* RMSSD */}
            {(() => {
              const s = getRMSSDStatus(vitals.RMSSD);

              return (
                <div className="health-card">
                  <span className="pill" style={{ background: s.color }}>
                    {mapLabel(s.label)}
                  </span>

                  <h2>{formatNum(vitals.RMSSD)} <span>ms</span></h2>

                  <div className="scale">
                    <span className={s.active === "Low" ? "active" : ""}>{t.LOW}</span>
                    <span className={s.active === "Healthy" ? "active" : ""}>{t.HEALTHY}</span>
                    <span className={s.active === "Optimal" ? "active" : ""}>{t.OPTIMAL}</span>
                  </div>

                  <small>{t.RMSSD_DESC}</small>
                </div>
              );
            })()}

            {/* {console.log(vitals.LFHF)} */}
            {Number.isFinite(Number(vitals.LFHF)) && Number(vitals.LFHF) > 0 && 
            (() => {
              console.log(vitals.LFHF, "vitdddals.LFHF")
              const s = getLFHFStatus(vitals.LFHF);

              return (
                <div className="health-card">
                  <span className="pill" style={{ background: s.color }}>
                    {s.active === "Relaxed"
                      ? t.RELAXED
                      : s.active === "Balanced"
                        ? t.BALANCED
                        : t.STRESSED}
                  </span>

                  <h2>{formatNum(vitals.LFHF, 2)}</h2>

                  <div className="scale">
                    <span className={s.active === "Relaxed" ? "active" : ""}>{t.RELAXED}</span>
                    <span className={s.active === "Balanced" ? "active" : ""}>{t.BALANCED}</span>
                    <span className={s.active === "Stressed" ? "active" : ""}>{t.STRESSED}</span>
                  </div>

                  <small>{t.LFHF_DESC}</small>
                </div>
              );
            })()}

        


            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={waveGraphData}>
                <Line dataKey="value" dot={false} />
                <XAxis hide />
                <YAxis hide />
                <Tooltip />
              </LineChart>
            </ResponsiveContainer>


            
     <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-xl text-left shadow-sm">
  <p className="text-sm text-gray-700 leading-relaxed">
    <span className="font-semibold text-yellow-700">
      ⚠️ {lang === "hi" ? "अस्वीकरण:" : "Disclaimer:"}
    </span>{" "}
    {SIMPLE_DISCLAIMER[lang]}
  </p>
</div>



            <button  onClick={() => {
    // clear auth
    localStorage.removeItem("token");
    localStorage.removeItem("patient_id");

    // redirect to login
    navigate("/login");
  }}>
              🔁 {T.scanAgain}
            </button>
          </div>
        )}
      </div>

  );
}


